// src/core/hooks/usePhoneVerification.js
import { useState, useCallback, useEffect } from 'react';
import { twilioService } from '../services/twilio';
import { useTranslation } from 'react-i18next';
import { ENV } from '../config/environment.config';

// Code OTP par défaut pour testeurs beta/dev (ne jamais activer en production)
const BETA_OTP_CODE = '123456';

export const usePhoneVerification = () => {
  const { t } = useTranslation('onboarding');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verifiedPhone, setVerifiedPhone] = useState('');

  // Timer pour le cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    
    const timer = setInterval(() => {
      setCooldown(prev => Math.max(0, prev - 1000));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [cooldown]);

  const sendVerificationCode = useCallback(async (phoneNumber, channel = 'sms') => {
    if (cooldown > 0) {
      setError(t('phone.errors.too_many_attempts'));
      return { success: false };
    }

    // Bypass Twilio en beta/dev — simule l'envoi sans SMS réel
    if (!ENV.isProd) {
      setVerificationSent(true);
      setVerifiedPhone(phoneNumber);
      setCooldown(60 * 1000);
      return { success: true };
    }

    try {
      setIsLoading(true);
      setError('');

      // Envoi du code via le service Twilio
      const result = await twilioService.sendVerificationCode(phoneNumber, channel);
      
      if (result.success) {
        setVerificationSent(true);
        setVerifiedPhone(phoneNumber);
        setCooldown(60 * 1000); // 1 minute de cooldown
        return { success: true };
      } else {
        setError(t('phone.errors.send_failed'));
        return { success: false };
      }
    } catch (error) {
      console.error('Error sending verification:', error);
      
      if (error.message.includes('invalid')) {
        setError(t('phone.errors.invalid_number'));
      } else if (error.message.includes('too many')) {
        setError(t('phone.errors.too_many_requests'));
        setCooldown(300 * 1000); // 5 minutes de cooldown
      } else {
        setError(t('phone.errors.send_failed'));
      }
      
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  }, [t, cooldown]);

  const verifyCode = useCallback(async (code, channel = 'sms') => {
    if (!verifiedPhone) {
      setError(t('phone.errors.no_phone'));
      return { success: false };
    }

    // Bypass Twilio en beta/dev — accepte uniquement le code par défaut
    if (!ENV.isProd) {
      if (code === BETA_OTP_CODE) {
        return { success: true, phone: verifiedPhone, phoneVerified: true };
      }
      setError(t('phone.errors.invalid_code'));
      return { success: false };
    }

    try {
      setIsVerifying(true);
      setError('');

      // Vérification du code via le service Twilio
      const result = await twilioService.verifyCode(verifiedPhone, code, channel);
      
      if (result.success) {
        return {
          success: true,
          phone: verifiedPhone,
          phoneVerified: true
        };
      } else {
        setError(t('phone.errors.invalid_code'));
        return { success: false };
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      
      if (error.message.includes('invalid')) {
        setError(t('phone.errors.invalid_code'));
      } else if (error.message.includes('expired')) {
        setError(t('phone.errors.code_expired'));
      } else {
        setError(t('phone.errors.verification_failed'));
      }
      
      return { success: false };
    } finally {
      setIsVerifying(false);
    }
  }, [verifiedPhone, t]);

  const resetVerification = useCallback(() => {
    setVerificationSent(false);
    setVerifiedPhone('');
    setError('');
  }, []);

  return {
    sendVerificationCode,
    verifyCode,
    resetVerification,
    isLoading,
    isVerifying,
    error,
    verificationSent,
    cooldown,
    cooldownRemaining: Math.ceil(cooldown / 1000)
  };
};